import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { collectionService } from "@/services";
import type { CollectionDashboardData, GroupCollectionRow, ReceiptView, Member, MemberAccountTransaction } from "@/services/types";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { StatCard } from "@/components/base/StatCard";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { EmptyState } from "@/components/base/EmptyState";
import { ErrorState } from "@/components/base/ErrorState";
import { DataTable, type DataTableColumn } from "@/components/feature/DataTable";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate, formatDateTime } from "@/utils/format";
import { paymentModeLabel } from "./components/labels";
import { CollectionDepositModal } from "./components/CollectionDepositModal";
import { AccountReceiptModal } from "../members/profile/components/AccountReceiptModal";

function ProgressCell({ row }: { row: GroupCollectionRow }) {
  const pct = row.expected > 0 ? Math.round((row.collected / row.expected) * 100) : 0;
  const tone = row.expected > 0 && row.collected >= row.expected ? "bg-success-500" : "bg-accent-500";
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-background-200">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className="tnum whitespace-nowrap text-xs text-foreground-600">{pct}%</span>
    </div>
  );
}

function NextAuctionCell({ row }: { row: GroupCollectionRow }) {
  if (row.isComplete) return <span className="text-xs text-foreground-400">Complete</span>;
  if (row.nextAuctionDate) {
    return <span className="tnum text-xs font-medium text-foreground-700">{formatDate(row.nextAuctionDate)}</span>;
  }
  return <span className="text-xs text-foreground-400">—</span>;
}

interface MonthCollectionSummary {
  monthKey: string;
  periodLabel: string;
  groupsCount: number;
  groups: GroupCollectionRow[];
  totalExpected: number;
  totalCollected: number;
  totalOutstanding: number;
  paidMembers: number;
  totalMembers: number;
  collectionRate: number;
}

interface CycleCollectionSummary {
  cycleKey: string;
  cycle: number;
  cycleLabel: string;
  groupsCount: number;
  groups: GroupCollectionRow[];
  totalExpected: number;
  totalCollected: number;
  totalOutstanding: number;
  paidMembers: number;
  totalMembers: number;
  collectionRate: number;
}

export default function CollectionPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<CollectionDashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"month" | "cycle" | "group">("month");
  const [selectedMonth, setSelectedMonth] = useState<string>("all");
  const [selectedCycle, setSelectedCycle] = useState<string>("all");
  const [search, setSearch] = useState<string>("");
  const [expandedMonth, setExpandedMonth] = useState<string | null>(null);
  const [expandedCycle, setExpandedCycle] = useState<string | null>(null);

  // Deposit advance modal & receipt modal
  const [depositOpen, setDepositOpen] = useState(false);
  const [receiptTx, setReceiptTx] = useState<MemberAccountTransaction | null>(null);
  const [receiptMember, setReceiptMember] = useState<Member | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await collectionService.getCollectionDashboard());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load collection data.");
      setData(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const availableMonths = useMemo(() => {
    if (!data) return [];
    const months = new Set<string>();
    for (const g of data.groups) {
      if (g.periodLabel) months.add(g.periodLabel);
    }
    return Array.from(months);
  }, [data]);

  const availableCycles = useMemo(() => {
    if (!data) return [];
    const cycles = new Set<number>();
    for (const g of data.groups) {
      if (typeof g.cycle === "number") cycles.add(g.cycle);
    }
    return Array.from(cycles).sort((a, b) => a - b);
  }, [data]);

  const normalizedSearch = search.trim().toLowerCase();

  const monthSummaries = useMemo<MonthCollectionSummary[]>(() => {
    if (!data) return [];
    const map = new Map<string, MonthCollectionSummary>();

    let list = data.groups;
    if (selectedCycle !== "all") {
      list = list.filter((g) => String(g.cycle) === selectedCycle);
    }
    if (normalizedSearch) {
      list = list.filter(
        (g) =>
          g.groupName.toLowerCase().includes(normalizedSearch) ||
          g.groupCode.toLowerCase().includes(normalizedSearch)
      );
    }

    for (const g of list) {
      const key = g.periodLabel || "Current Month";
      const existing = map.get(key);
      if (!existing) {
        map.set(key, {
          monthKey: key,
          periodLabel: key,
          groupsCount: 1,
          groups: [g],
          totalExpected: g.expected,
          totalCollected: g.collected,
          totalOutstanding: Math.max(0, g.expected - g.collected),
          paidMembers: g.paidMembers,
          totalMembers: g.totalMembers,
          collectionRate: g.expected > 0 ? (g.collected / g.expected) * 100 : 0,
        });
      } else {
        existing.groupsCount += 1;
        existing.groups.push(g);
        existing.totalExpected += g.expected;
        existing.totalCollected += g.collected;
        existing.totalOutstanding += Math.max(0, g.expected - g.collected);
        existing.paidMembers += g.paidMembers;
        existing.totalMembers += g.totalMembers;
        existing.collectionRate =
          existing.totalExpected > 0 ? (existing.totalCollected / existing.totalExpected) * 100 : 0;
      }
    }

    let summaries = Array.from(map.values());
    if (selectedMonth !== "all") {
      summaries = summaries.filter((s) => s.periodLabel === selectedMonth);
    }
    return summaries;
  }, [data, selectedMonth, selectedCycle, normalizedSearch]);

  const cycleSummaries = useMemo<CycleCollectionSummary[]>(() => {
    if (!data) return [];
    const map = new Map<number, CycleCollectionSummary>();

    let list = data.groups;
    if (selectedMonth !== "all") {
      list = list.filter((g) => g.periodLabel === selectedMonth);
    }
    if (selectedCycle !== "all") {
      list = list.filter((g) => String(g.cycle) === selectedCycle);
    }
    if (normalizedSearch) {
      list = list.filter(
        (g) =>
          g.groupName.toLowerCase().includes(normalizedSearch) ||
          g.groupCode.toLowerCase().includes(normalizedSearch)
      );
    }

    for (const g of list) {
      const cycleNum = g.cycle ?? 1;
      const key = `Cycle ${cycleNum}`;
      const existing = map.get(cycleNum);
      if (!existing) {
        map.set(cycleNum, {
          cycleKey: String(cycleNum),
          cycle: cycleNum,
          cycleLabel: key,
          groupsCount: 1,
          groups: [g],
          totalExpected: g.expected,
          totalCollected: g.collected,
          totalOutstanding: Math.max(0, g.expected - g.collected),
          paidMembers: g.paidMembers,
          totalMembers: g.totalMembers,
          collectionRate: g.expected > 0 ? (g.collected / g.expected) * 100 : 0,
        });
      } else {
        existing.groupsCount += 1;
        existing.groups.push(g);
        existing.totalExpected += g.expected;
        existing.totalCollected += g.collected;
        existing.totalOutstanding += Math.max(0, g.expected - g.collected);
        existing.paidMembers += g.paidMembers;
        existing.totalMembers += g.totalMembers;
        existing.collectionRate =
          existing.totalExpected > 0 ? (existing.totalCollected / existing.totalExpected) * 100 : 0;
      }
    }

    return Array.from(map.values()).sort((a, b) => a.cycle - b.cycle);
  }, [data, selectedMonth, selectedCycle, normalizedSearch]);

  const filteredGroups = useMemo(() => {
    if (!data) return [];
    let list = data.groups;
    if (selectedMonth !== "all") {
      list = list.filter((g) => g.periodLabel === selectedMonth);
    }
    if (selectedCycle !== "all") {
      list = list.filter((g) => String(g.cycle) === selectedCycle);
    }
    if (normalizedSearch) {
      list = list.filter(
        (g) =>
          g.groupName.toLowerCase().includes(normalizedSearch) ||
          g.groupCode.toLowerCase().includes(normalizedSearch)
      );
    }
    return list;
  }, [data, selectedMonth, selectedCycle, normalizedSearch]);

  const todayRows = useMemo(() => {
    if (!data) return [];
    return [...data.todayReceipts].sort((a, b) => b.receivedOn.localeCompare(a.receivedOn));
  }, [data]);

  const monthColumns: DataTableColumn<MonthCollectionSummary>[] = [
    {
      id: "month",
      header: "Month / Period",
      accessor: (m) => (
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-accent-100 text-accent-700">
            <i className="ri-calendar-event-line text-base" />
          </div>
          <div>
            <span className="font-semibold text-foreground-900">{m.periodLabel}</span>
            <p className="text-xs text-foreground-400">
              {m.groupsCount} {m.groupsCount === 1 ? "group active" : "groups active"}
            </p>
          </div>
        </div>
      ),
      sortValue: (m) => m.periodLabel,
      sortable: true,
    },
    {
      id: "demand",
      header: "Total Demand",
      align: "right",
      accessor: (m) => (
        <span className="font-medium text-foreground-900">
          <CurrencyDisplay amount={m.totalExpected} hideSymbol />
        </span>
      ),
      sortValue: (m) => m.totalExpected,
      sortable: true,
    },
    {
      id: "collected",
      header: "Total Collected",
      align: "right",
      accessor: (m) => (
        <span className="font-semibold text-success-700">
          <CurrencyDisplay amount={m.totalCollected} hideSymbol />
        </span>
      ),
      sortValue: (m) => m.totalCollected,
      sortable: true,
    },
    {
      id: "outstanding",
      header: "Pending / Balance",
      align: "right",
      accessor: (m) => (
        <span className={`font-medium ${m.totalOutstanding > 0 ? "text-warning-700" : "text-foreground-400"}`}>
          <CurrencyDisplay amount={m.totalOutstanding} hideSymbol />
        </span>
      ),
      sortValue: (m) => m.totalOutstanding,
      sortable: true,
    },
    {
      id: "members",
      header: "Paid Members",
      align: "right",
      accessor: (m) => (
        <span className="tnum whitespace-nowrap text-xs text-foreground-600">
          <span className="font-semibold text-foreground-900">{m.paidMembers}</span>/{m.totalMembers}
          <span className="ml-1 text-foreground-400">({m.totalMembers > 0 ? Math.round((m.paidMembers / m.totalMembers) * 100) : 0}%)</span>
        </span>
      ),
      sortValue: (m) => m.paidMembers,
      sortable: true,
    },
    {
      id: "progress",
      header: "Collection Rate",
      accessor: (m) => {
        const pct = Math.round(m.collectionRate);
        const tone = pct >= 100 ? "bg-success-500" : "bg-accent-500";
        return (
          <div className="flex items-center gap-2">
            <div className="h-2 w-24 overflow-hidden rounded-full bg-background-200">
              <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.min(pct, 100)}%` }} />
            </div>
            <span className="tnum whitespace-nowrap text-xs font-semibold text-foreground-700">{pct}%</span>
          </div>
        );
      },
      sortValue: (m) => m.collectionRate,
      sortable: true,
    },
    {
      id: "actions",
      header: "",
      align: "right",
      width: "120px",
      hideable: false,
      accessor: (m) => (
        <Button
          size="sm"
          variant="outline"
          icon={<i className={expandedMonth === m.monthKey ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />}
          onClick={(e) => {
            e.stopPropagation();
            setExpandedMonth(expandedMonth === m.monthKey ? null : m.monthKey);
          }}
        >
          {expandedMonth === m.monthKey ? "Hide groups" : "View groups"}
        </Button>
      ),
    },
  ];

  const cycleColumns: DataTableColumn<CycleCollectionSummary>[] = [
    {
      id: "cycle",
      header: "Cycle",
      accessor: (c) => (
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
            <i className="ri-repeat-2-line text-base" />
          </div>
          <div>
            <span className="font-semibold text-foreground-900">{c.cycleLabel}</span>
            <p className="text-xs text-foreground-400">
              {c.groupsCount} {c.groupsCount === 1 ? "group active" : "groups active"}
            </p>
          </div>
        </div>
      ),
      sortValue: (c) => c.cycle,
      sortable: true,
    },
    {
      id: "demand",
      header: "Total Demand",
      align: "right",
      accessor: (c) => (
        <span className="font-medium text-foreground-900">
          <CurrencyDisplay amount={c.totalExpected} hideSymbol />
        </span>
      ),
      sortValue: (c) => c.totalExpected,
      sortable: true,
    },
    {
      id: "collected",
      header: "Total Collected",
      align: "right",
      accessor: (c) => (
        <span className="font-semibold text-success-700">
          <CurrencyDisplay amount={c.totalCollected} hideSymbol />
        </span>
      ),
      sortValue: (c) => c.totalCollected,
      sortable: true,
    },
    {
      id: "outstanding",
      header: "Pending / Balance",
      align: "right",
      accessor: (c) => (
        <span className={`font-medium ${c.totalOutstanding > 0 ? "text-warning-700" : "text-foreground-400"}`}>
          <CurrencyDisplay amount={c.totalOutstanding} hideSymbol />
        </span>
      ),
      sortValue: (c) => c.totalOutstanding,
      sortable: true,
    },
    {
      id: "members",
      header: "Paid Members",
      align: "right",
      accessor: (c) => (
        <span className="tnum whitespace-nowrap text-xs text-foreground-600">
          <span className="font-semibold text-foreground-900">{c.paidMembers}</span>/{c.totalMembers}
          <span className="ml-1 text-foreground-400">({c.totalMembers > 0 ? Math.round((c.paidMembers / c.totalMembers) * 100) : 0}%)</span>
        </span>
      ),
      sortValue: (c) => c.paidMembers,
      sortable: true,
    },
    {
      id: "progress",
      header: "Collection Rate",
      accessor: (c) => {
        const pct = Math.round(c.collectionRate);
        const tone = pct >= 100 ? "bg-success-500" : "bg-accent-500";
        return (
          <div className="flex items-center gap-2">
            <div className="h-2 w-24 overflow-hidden rounded-full bg-background-200">
              <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.min(pct, 100)}%` }} />
            </div>
            <span className="tnum whitespace-nowrap text-xs font-semibold text-foreground-700">{pct}%</span>
          </div>
        );
      },
      sortValue: (c) => c.collectionRate,
      sortable: true,
    },
    {
      id: "actions",
      header: "",
      align: "right",
      width: "120px",
      hideable: false,
      accessor: (c) => (
        <Button
          size="sm"
          variant="outline"
          icon={<i className={expandedCycle === c.cycleKey ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />}
          onClick={(e) => {
            e.stopPropagation();
            setExpandedCycle(expandedCycle === c.cycleKey ? null : c.cycleKey);
          }}
        >
          {expandedCycle === c.cycleKey ? "Hide groups" : "View groups"}
        </Button>
      ),
    },
  ];

  const groupColumns: DataTableColumn<GroupCollectionRow>[] = [
    {
      id: "group",
      header: "Group",
      accessor: (g) => (
        <div>
          <span className="font-medium text-foreground-900">{g.groupName}</span>
          <span className="tnum ml-2 text-xs text-foreground-400">{g.groupCode}</span>
        </div>
      ),
      sortValue: (g) => g.groupName,
      sortable: true,
    },
    {
      id: "month",
      header: "Month",
      accessor: (g) => (
        <Badge variant="neutral" className="text-xs font-semibold">
          {g.periodLabel || `Cycle ${g.cycle}`}
        </Badge>
      ),
      sortValue: (g) => g.periodLabel,
      sortable: true,
    },
    {
      id: "cycle",
      header: "Cycle",
      accessor: (g) => (
        <span className="whitespace-nowrap text-sm">
          Cycle {g.cycle}
        </span>
      ),
      sortValue: (g) => g.cycle,
      sortable: true,
    },
    {
      id: "demand",
      header: "Demand / member",
      align: "right",
      accessor: (g) => <CurrencyDisplay amount={g.demandPerMember} hideSymbol />,
      sortValue: (g) => g.demandPerMember,
      sortable: true,
    },
    {
      id: "paid",
      header: "Paid members",
      align: "right",
      accessor: (g) => (
        <span className="tnum whitespace-nowrap text-xs text-foreground-600">
          <span className="font-medium text-foreground-900">{g.paidMembers}</span>/{g.totalMembers}
        </span>
      ),
      sortValue: (g) => g.paidMembers,
      sortable: true,
    },
    {
      id: "collected",
      header: "Collected / expected",
      align: "right",
      accessor: (g) => (
        <div className="flex items-center justify-end gap-2">
          <CurrencyDisplay amount={g.collected} hideSymbol />
          <span className="text-foreground-300">/</span>
          <CurrencyDisplay amount={g.expected} hideSymbol />
        </div>
      ),
      sortValue: (g) => g.collected,
      sortable: true,
    },
    {
      id: "progress",
      header: "Progress",
      accessor: (g) => <ProgressCell row={g} />,
      sortValue: (g) => (g.expected > 0 ? g.collected / g.expected : 0),
      sortable: true,
    },
    {
      id: "next",
      header: "Next auction",
      accessor: (g) => <NextAuctionCell row={g} />,
    },
    {
      id: "sheet",
      header: "Collection Sheet",
      align: "center",
      width: "96px",
      hideable: false,
      accessor: (g) => (
        <Button
          size="sm"
          variant="outline"
          icon={<i className="ri-wallet-3-line" />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/collection/groups/${g.groupId}`);
          }}
        >
          Sheet
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Collection" isLast />
          </>
        }
        title="Collection"
        subtitle={data ? `${data.todayCollection === 0 ? "Nothing collected" : `₹${data.todayCollection.toLocaleString("en-IN")} collected`} today` : "Daily cash entry"}
        actions={
          <>
            <Button variant="outline" size="sm" icon={<i className="ri-file-list-3-line" />} onClick={() => navigate("/collection/day-book")}>
              Day book
            </Button>
            {/* <Button variant="outline" size="sm" icon={<i className="ri-alarm-line" />} onClick={() => navigate("/collection/late-fees")}>
              Late fees
            </Button> */}
            <Button
              variant="outline"
              size="sm"
              icon={<i className="ri-wallet-3-line" />}
              className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 focus-visible:outline-emerald-600 font-semibold"
              onClick={() => setDepositOpen(true)}
            >
              + Add deposit
            </Button>
            <Button icon={<i className="ri-add-line" />} onClick={() => navigate("/collection/receipt")}>
              Record receipt
            </Button>
          </>
        }
      />

      {error && !data ? (
        <Card>
          <ErrorState message={error} onRetry={load} />
        </Card>
      ) : !data ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-24 animate-pulse rounded-lg bg-background-200" />
            ))}
          </div>
          <div className="h-64 animate-pulse rounded-lg bg-background-200" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Today's collection"
              value={<CurrencyDisplay amount={data.todayCollection} hideSymbol />}
              icon="ri-wallet-3-line"
              iconClass="bg-success-100 text-success-600"
              tone="success"
            />
            <StatCard
              label="This month"
              value={<CurrencyDisplay amount={data.monthCollection} hideSymbol />}
              icon="ri-calendar-check-line"
              iconClass="bg-accent-100 text-accent-700"
              tone="accent"
            />
            <StatCard
              label="Total outstanding"
              value={<CurrencyDisplay amount={data.totalOutstanding} hideSymbol />}
              icon="ri-bank-line"
              iconClass="bg-warning-100 text-warning-700"
              tone="warning"
            />
            <StatCard
              label="Overdue demands"
              value={data.overdueCount}
              icon="ri-alarm-warning-line"
              iconClass="bg-danger-100 text-danger-600"
              tone="danger"
            />
          </div>

          <Card
            title={
              viewMode === "month"
                ? "Month-wise Collection Summary"
                : viewMode === "cycle"
                ? "Cycle-wise Collection Summary"
                : "Group-wise Collection Progress"
            }
            subtitle={
              viewMode === "month"
                ? "Aggregated collection and demand across all groups per month"
                : viewMode === "cycle"
                ? "Aggregated collection and demand across all groups per cycle"
                : "Latest collection progress for each active group"
            }
            actions={
              <div className="flex w-full flex-wrap items-center gap-2 lg:w-auto">
                {/* Search Bar */}
                <div className="relative flex w-full items-center sm:w-48 md:w-56">
                  <i className="ri-search-line pointer-events-none absolute left-2.5 text-xs text-foreground-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search active groups…"
                    className="h-8 w-full rounded-md border border-background-300 bg-white pl-7 pr-7 text-xs font-medium text-foreground-800 placeholder-foreground-400 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  {data === null ? (
                    <span className="pointer-events-none absolute right-2 text-primary-600">
                      <i className="ri-loader-4-line animate-spin text-sm" />
                    </span>
                  ) : search ? (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      className="absolute right-2 cursor-pointer text-foreground-400 hover:text-foreground-700"
                      title="Clear search"
                    >
                      <i className="ri-close-line text-xs" />
                    </button>
                  ) : null}
                </div>

                {availableMonths.length > 0 ? (
                  <div className="flex flex-1 items-center gap-1.5 text-xs sm:flex-initial">
                    <span className="text-foreground-500 font-medium">Month:</span>
                    <select
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      className="h-8 w-full rounded-md border border-background-300 bg-white px-2 py-1 text-xs font-medium text-foreground-800 shadow-sm focus:border-primary-500 focus:outline-none sm:w-auto"
                    >
                      <option value="all">All Months ({availableMonths.length})</option>
                      {availableMonths.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}

                {availableCycles.length > 0 ? (
                  <div className="flex flex-1 items-center gap-1.5 text-xs sm:flex-initial">
                    <span className="text-foreground-500 font-medium">Cycle:</span>
                    <select
                      value={selectedCycle}
                      onChange={(e) => setSelectedCycle(e.target.value)}
                      className="h-8 w-full rounded-md border border-background-300 bg-white px-2 py-1 text-xs font-medium text-foreground-800 shadow-sm focus:border-primary-500 focus:outline-none sm:w-auto"
                    >
                      <option value="all">All Cycles ({availableCycles.length})</option>
                      {availableCycles.map((c) => (
                        <option key={c} value={String(c)}>
                          Cycle {c}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}

                <div className="flex w-full items-center rounded-md border border-background-200 bg-background-100 p-0.5 text-xs sm:w-auto">
                  <button
                    type="button"
                    className={`flex-1 cursor-pointer rounded px-2.5 py-1 text-center font-medium transition-colors sm:flex-initial ${
                      viewMode === "month" ? "bg-white font-semibold text-foreground-900 shadow-sm" : "text-foreground-600 hover:text-foreground-900"
                    }`}
                    onClick={() => setViewMode("month")}
                  >
                    <i className="ri-calendar-line mr-1 text-xs" />
                    Month-wise
                  </button>
                  <button
                    type="button"
                    className={`flex-1 cursor-pointer rounded px-2.5 py-1 text-center font-medium transition-colors sm:flex-initial ${
                      viewMode === "cycle" ? "bg-white font-semibold text-foreground-900 shadow-sm" : "text-foreground-600 hover:text-foreground-900"
                    }`}
                    onClick={() => setViewMode("cycle")}
                  >
                    <i className="ri-repeat-2-line mr-1 text-xs" />
                    Cycle-wise
                  </button>
                  <button
                    type="button"
                    className={`flex-1 cursor-pointer rounded px-2.5 py-1 text-center font-medium transition-colors sm:flex-initial ${
                      viewMode === "group" ? "bg-white font-semibold text-foreground-900 shadow-sm" : "text-foreground-600 hover:text-foreground-900"
                    }`}
                    onClick={() => setViewMode("group")}
                  >
                    <i className="ri-group-line mr-1 text-xs" />
                    Group-wise
                  </button>
                </div>
              </div>
            }
          >
            {viewMode === "month" ? (
              <div className="space-y-3">
                <DataTable<MonthCollectionSummary>
                  columns={monthColumns}
                  rows={monthSummaries}
                  rowKey={(m) => m.monthKey}
                  emptyMessage={search ? "No matching groups found" : "No collection data"}
                  emptyDescription={
                    search
                      ? `No active groups match "${search}". Try checking the spelling or clearing search.`
                      : "Decided cycles for active groups will appear organized by month here."
                  }
                  onRowClick={(m) => setExpandedMonth(expandedMonth === m.monthKey ? null : m.monthKey)}
                  showTotals
                  totalLabel="Total across months"
                  exportable
                  exportFilename={`month-wise-collection-${selectedMonth}-cycle-${selectedCycle}`}
                  defaultPageSize={10}
                />

                {/* Expanded Month Group Details */}
                {expandedMonth ? (
                  <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50/40 p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-foreground-900">
                          Active Groups in {expandedMonth}
                        </span>
                        <Badge variant="accent">
                          {monthSummaries.find((s) => s.monthKey === expandedMonth)?.groupsCount ?? 0} groups
                        </Badge>
                      </div>
                      <Button size="sm" variant="ghost" icon={<i className="ri-close-line" />} onClick={() => setExpandedMonth(null)}>
                        Close
                      </Button>
                    </div>

                    <DataTable<GroupCollectionRow>
                      columns={groupColumns}
                      rows={monthSummaries.find((s) => s.monthKey === expandedMonth)?.groups ?? []}
                      rowKey={(g) => g.groupId}
                      emptyMessage={search ? "No matching groups in this month" : "No active groups"}
                      emptyDescription={
                        search
                          ? `No groups in ${expandedMonth} match "${search}".`
                          : "No active groups found for this month."
                      }
                      onRowClick={(g) => navigate(`/collection/groups/${g.groupId}`)}
                      showTotals
                      totalLabel={`Total for ${expandedMonth}`}
                      exportable
                      exportFilename={`active-groups-${expandedMonth}`}
                      defaultPageSize={10}
                    />
                  </div>
                ) : null}
              </div>
            ) : viewMode === "cycle" ? (
              <div className="space-y-3">
                <DataTable<CycleCollectionSummary>
                  columns={cycleColumns}
                  rows={cycleSummaries}
                  rowKey={(c) => c.cycleKey}
                  emptyMessage={search ? "No matching groups found" : "No collection data"}
                  emptyDescription={
                    search
                      ? `No active groups match "${search}". Try checking the spelling or clearing search.`
                      : "Decided cycles for active groups will appear organized by cycle here."
                  }
                  onRowClick={(c) => setExpandedCycle(expandedCycle === c.cycleKey ? null : c.cycleKey)}
                  showTotals
                  totalLabel="Total across cycles"
                  exportable
                  exportFilename={`cycle-wise-collection-cycle-${selectedCycle}-month-${selectedMonth}`}
                  defaultPageSize={10}
                />

                {/* Expanded Cycle Group Details */}
                {expandedCycle ? (
                  <div className="mt-4 rounded-lg border border-secondary-200 bg-secondary-50/40 p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-foreground-900">
                          Active Groups in Cycle {expandedCycle}
                        </span>
                        <Badge variant="neutral">
                          {cycleSummaries.find((s) => s.cycleKey === expandedCycle)?.groupsCount ?? 0} groups
                        </Badge>
                      </div>
                      <Button size="sm" variant="ghost" icon={<i className="ri-close-line" />} onClick={() => setExpandedCycle(null)}>
                        Close
                      </Button>
                    </div>

                    <DataTable<GroupCollectionRow>
                      columns={groupColumns}
                      rows={cycleSummaries.find((s) => s.cycleKey === expandedCycle)?.groups ?? []}
                      rowKey={(g) => g.groupId}
                      emptyMessage={search ? "No matching groups in this cycle" : "No active groups"}
                      emptyDescription={
                        search
                          ? `No groups in Cycle ${expandedCycle} match "${search}".`
                          : "No active groups found for this cycle."
                      }
                      onRowClick={(g) => navigate(`/collection/groups/${g.groupId}`)}
                      showTotals
                      totalLabel={`Total for Cycle ${expandedCycle}`}
                      exportable
                      exportFilename={`active-groups-cycle-${expandedCycle}`}
                      defaultPageSize={10}
                    />
                  </div>
                ) : null}
              </div>
            ) : (
              <DataTable<GroupCollectionRow>
                columns={groupColumns}
                rows={filteredGroups}
                rowKey={(g) => g.groupId}
                emptyMessage={search ? "No matching groups found" : "No active groups for this filter"}
                emptyDescription={
                  search
                    ? `No active groups match "${search}". Try clearing or refining your search.`
                    : "Active groups appear here once their first cycle is decided."
                }
                onRowClick={(g) => navigate(`/collection/groups/${g.groupId}`)}
                showTotals
                totalLabel="Total"
                exportable
                exportFilename={`group-collection-month-${selectedMonth}-cycle-${selectedCycle}`}
                defaultPageSize={10}
              />
            )}
          </Card>

          <Card
            title="Today's receipts"
            subtitle={data.todayReceipts.length ? `${data.todayReceipts.filter((r) => !r.reversedAt).length} collected today` : "Nothing collected yet today"}
            actions={
              <Link to="/collection/day-book" className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:underline">
                Open day book <i className="ri-arrow-right-line" />
              </Link>
            }
          >
            {todayRows.length === 0 ? (
              <EmptyState
                icon="ri-receipt-line"
                title="No receipts yet today"
                description="Record the first payment of the day, or check the day book for previous days."
                action={
                  <Button size="sm" icon={<i className="ri-add-line" />} onClick={() => navigate("/collection/receipt")}>
                    Record receipt
                  </Button>
                }
              />
            ) : (
              <div className="divide-y divide-background-100">
                {todayRows.map((r: ReceiptView) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => navigate(`/collection/receipts/${r.id}`)}
                    className="flex w-full cursor-pointer items-center justify-between gap-3 py-2.5 text-left hover:bg-background-100/60"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                        <i className="ri-receipt-line text-base" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground-900">
                          {r.memberName}
                          <span className="tnum ml-2 text-xs font-normal text-foreground-400">{r.receiptNumber}</span>
                        </p>
                        <p className="truncate text-xs text-foreground-500">
                          {r.lines.map((l) => `${l.groupName} (${l.cycle})`).join(" · ")}
                          {r.reversedAt ? <span className="ml-2 text-danger-600">Reversed</span> : null}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-none items-center gap-4">
                      <div className="text-right">
                        <p className="tnum text-sm font-semibold text-foreground-900">
                          <CurrencyDisplay amount={r.total} hideSymbol />
                        </p>
                        <p className="text-xs text-foreground-500">
                          {paymentModeLabel(r.mode)} · {formatDateTime(r.createdAt || r.receivedOn)}
                        </p>
                      </div>
                      {r.reversedAt ? <Badge variant="overdue">Reversed</Badge> : <Badge variant="paid" dot>Paid</Badge>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Advance Deposit Modal */}
      {depositOpen ? (
        <CollectionDepositModal
          open
          onClose={() => setDepositOpen(false)}
          onSuccess={(tx, member) => {
            load();
            setReceiptTx(tx);
            setReceiptMember(member);
          }}
        />
      ) : null}

      {/* Account Receipt Slip Modal */}
      {receiptTx && receiptMember ? (
        <AccountReceiptModal
          open
          onClose={() => {
            setReceiptTx(null);
            setReceiptMember(null);
          }}
          member={receiptMember}
          transaction={receiptTx}
        />
      ) : null}
    </div>
  );
}